import { Box, Table, TableHead, TableBody, TableRow, TableCell, Typography, makeStyles } from '@material-ui/core';
import SlideFrame from './SlideFrame';
import useTableRules from '../UI/useTableRules';
import { CONTESTANT_COLORS } from './streamPalette';
import durationToString from '../../utils/durationToString';

// A contestant's three columns are the same width as the other's, so the two
// halves of the table can be read against each other
const SOLVE_COLUMN = 140;
const TIME_COLUMN = 165;
const ATTAINED_COLUMN = 135;

const useStyles = makeStyles({
    table: {
        width: 'auto',
        tableLayout: 'fixed'
    },
    cell: {
        fontSize: '1.7rem',
        whiteSpace: 'nowrap',
        padding: '12px 14px'
    },
    // a heading is allowed to take two lines, so its column stays as narrow as
    // the numbers under it need
    head: {
        fontSize: '1.4rem',
        padding: '12px 14px'
    },
    name: {
        fontSize: '2rem'
    }
});

// How long each of them has taken to get to their nth solve of a match: the
// fastest they have managed, what they average, and how many matches they got
// that far in at all. Both are read off one table, three columns to a contestant.
function PaceSlide(props) {
    const classes = useStyles();
    const rules = useTableRules();
    const sides = [
        { name: props.contestant1, pace: props.statistics1?.pace ?? [] },
        { name: props.contestant2, pace: props.statistics2?.pace ?? [] }
    ];
    const rows = Math.max(...sides.map(side => side.pace.length), 0);

    return (
        <SlideFrame eyebrow={props.tournamentName} matchLabel={props.matchLabel} title='Pace'>
            <Box display='flex' justifyContent='center'>
            <Table className={`${classes.table} ${rules.ruled}`}>
                <colgroup>
                    <col style={{ width: SOLVE_COLUMN }} />
                    {sides.map((_side, index) => [
                        <col key={`${index}-fastest`} style={{ width: TIME_COLUMN }} />,
                        <col key={`${index}-average`} style={{ width: TIME_COLUMN }} />,
                        <col key={`${index}-attained`} style={{ width: ATTAINED_COLUMN }} />
                    ])}
                </colgroup>
                <TableHead>
                    <TableRow>
                        <TableCell />
                        {sides.map((side, index) => (
                            <TableCell key={index} colSpan={3} align='center'>
                                <Typography className={classes.name} style={{ color: CONTESTANT_COLORS[index] }} noWrap>
                                    {side.name}
                                </Typography>
                            </TableCell>
                        ))}
                    </TableRow>
                    <TableRow>
                        <TableCell className={classes.head}> Solve Count </TableCell>
                        {sides.map((_side, index) => [
                            <TableCell key={`${index}-fastest`} className={classes.head} align='right'> Fastest Time </TableCell>,
                            <TableCell key={`${index}-average`} className={classes.head} align='right'> Average Time </TableCell>,
                            <TableCell key={`${index}-attained`} className={classes.head} align='right'> Attained </TableCell>
                        ])}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {new Array(rows).fill(null).map((_row, index) => (
                        <TableRow key={index}>
                            <TableCell className={classes.cell}> {index + 1} </TableCell>
                            {sides.map((side, sideIndex) => {
                                const pace = side.pace[index];
                                return [
                                    <TableCell key={`${sideIndex}-fastest`} align='right' className={classes.cell}>
                                        {durationToString(pace?.fastest)}
                                    </TableCell>,
                                    <TableCell key={`${sideIndex}-average`} align='right' className={classes.cell}>
                                        {durationToString(pace?.average)}
                                    </TableCell>,
                                    <TableCell key={`${sideIndex}-attained`} align='right' className={classes.cell}>
                                        {pace?.matches ? pace.matches : '—'}
                                    </TableCell>
                                ];
                            })}
                        </TableRow>
                    ))}
                    {rows === 0 &&
                        <TableRow>
                            <TableCell colSpan={7}>
                                <Typography color='textSecondary'> Neither contestant has solved anything yet </Typography>
                            </TableCell>
                        </TableRow>
                    }
                </TableBody>
            </Table>
            </Box>
        </SlideFrame>
    );
}

export default PaceSlide;
